import HttpService from "./http.service";
import FastApiService from "./fastapi.service";

class CrudService {
  // User related functions
  getUsers = async () => {
    const usersEndpoint = "users";
    return await HttpService.get(usersEndpoint);
  };

  getUser = async (id) => {
    const endpoint = `users/${id}?include=roles`;
    return await HttpService.get(endpoint);
  };

  getUserWithPermissions = async (id) => {
    const endpoint = `users/${id}?include=roles,roles.permissions`;
    return await HttpService.get(endpoint);
  };

  createUser = async (payload) => {
    const endpoint = "users";
    return await HttpService.post(endpoint, payload);
  };

  updateUser = async (payload, id) => {
    const endpoint = `users/${id}`;
    return await HttpService.patch(endpoint, payload);
  };

  deleteUser = async (id) => {
    const endpoint = `users/${id}`;
    return await HttpService.delete(endpoint);
  };

  imageUpload = async (formData, id) => {
    const imageUpdate = `uploads/users/${id}/profile-image`;
    return await HttpService.post(imageUpdate, formData);
  };

  getPublicProfile = async (id) => {
    const endpoint = `users/publicProfile/${id}`;
    return await HttpService.get(endpoint);
  };

  // Role related functions
  getRoles = async () => {
    const rolesEndpoint = "roles";
    return await HttpService.get(rolesEndpoint);
  };

  getRole = async (id) => {
    const endpoint = `roles/${id}`;
    return await HttpService.get(endpoint);
  };

  createRole = async (payload) => {
    const endpoint = "roles";
    return await HttpService.post(endpoint, payload);
  };

  updateRole = async (payload, id) => {
    const endpoint = `roles/${id}`;
    return await HttpService.patch(endpoint, payload);
  };

  deleteRole = async (id) => {
    const endpoint = `roles/${id}`;
    return await HttpService.delete(endpoint);
  };

  // Category related functions
  getCategories = async () => {
    const categoriesEndpoint = "categories";
    return await HttpService.get(categoriesEndpoint);
  };

  getCategory = async (id) => {
    const categoriesEndpoint = `categories/${id}`;
    return await HttpService.get(categoriesEndpoint);
  };

  createCategory = async (payload) => {
    const endpoint = "categories";
    return await HttpService.post(endpoint, payload);
  };

  updateCategory = async (payload, id) => {
    const categoriesEndpoint = `categories/${id}`;
    return await HttpService.patch(categoriesEndpoint, payload);
  };

  deleteCategory = async (id) => {
    const endpoint = `categories/${id}`;
    return await HttpService.delete(endpoint);
  };

  // Tag related functions
  getTags = async () => {
    const tagsEndpoint = "tags";
    return await HttpService.get(tagsEndpoint);
  };

  getTag = async (id) => {
    const endpoint = `tags/${id}`;
    return await HttpService.get(endpoint);
  };

  createTag = async (payload) => {
    const endpoint = "tags";
    return await HttpService.post(endpoint, payload);
  };

  updateTag = async (payload, id) => {
    const endpoint = `tags/${id}`;
    return await HttpService.patch(endpoint, payload);
  };

  deleteTag = async (id) => {
    const endpoint = `tags/${id}`;
    return await HttpService.delete(endpoint);
  };

  // Item related functions
  getItems = async () => {
    const tagsEndpoint = "items";
    return await HttpService.get(tagsEndpoint);
  };

  getItem = async (id) => {
    const endpoint = `items/${id}?include=category,tags`;
    return await HttpService.get(endpoint);
  };

  createItem = async (payload) => {
    const endpoint = "items";
    return await HttpService.post(endpoint, payload);
  };

  updateItem = async (payload, id) => {
    const endpoint = `items/${id}`;
    return await HttpService.patch(endpoint, payload);
  };

  deleteItem = async (id) => {
    const endpoint = `items/${id}`;
    return await HttpService.delete(endpoint);
  };

  itemImageUpload = async (formData, id) => {
    const imageUpdate = `uploads/items/${id}/image`;
    return await HttpService.post(imageUpdate, formData);
  };

  getCategoryOfItem = async (id) => {
    const endpoint = `items/${id}/category`;
    return await HttpService.get(endpoint);
  };

  getTagsOfItem = async (id) => {
    const endpoint = `items/${id}/tags`;
    return await HttpService.get(endpoint);
  };

  // Persona related functions
  getPersonas = async () => {
    const endpoint = "personas";
    return await HttpService.get(endpoint);
  };

  getPersona = async (id) => {
    const endpoint = `personas/${id}`;
    return await HttpService.get(endpoint);
  };

  createPersona = async (payload) => {
    const endpoint = "personas";
    return await HttpService.post(endpoint, payload);
  };

  updatePersona = async (payload, id) => {
    const endpoint = `personas/${id}`;
    return await HttpService.patch(endpoint, payload);
  };

  deletePersona = async (id) => {
    const endpoint = `personas/${id}`;
    return await HttpService.delete(endpoint);
  };

  // Job related functions
  createJob = async (payload) => {
    const endpoint = "jobs";
    return await HttpService.post(endpoint, payload);
  };

  getJobsByPersona = async (id) => {
    const endpoint = `jobs/personaJobs/${id}`;
    return await HttpService.get(endpoint);
  };

  getJobDataById = async (id) => {
    const endpoint = `jobs/${id}`;
    return await HttpService.get(endpoint);
  };

  getVisitedJobs = async (id) => {
    const endpoint = `jobs/visitedJobs/${id}`;
    return await HttpService.get(endpoint);
  };

  addJobToVisitedJobs = async (payload) => {
    const endpoint = `jobs/addJobToVisitedJobs/`;
    const response = await HttpService.post(endpoint, payload);
    return response;
  };

  getVisitedJobById = async (id) => {
    const endpoint = `jobs/visitedJob/${id}`;
    return await HttpService.get(endpoint);
  };

  // Course related functions
  getCoursesSummaries = async () => {
    const endpoint = `courses/summaries`;
    return await FastApiService.get(endpoint);
  };

  getCourses = async () => {
    const endpoint = `courses`;
    return await HttpService.get(endpoint);
  };

  getCourseList = async () => {
    const endpoint = `courses`;
    return await FastApiService.get(endpoint);
  };

  getCourse = async (id, includeQuestionsFile = false) => {
    const endpoint = `courses/${id}?include_questions_file=${includeQuestionsFile}`;
    const response = await FastApiService.get(endpoint);
    return response;
  };

  createCourse = async (payload) => {
    const endpoint = "courses";
    return await HttpService.post(endpoint, payload);
  };

  editCourse = async (payload, id) => {
    const endpoint = `courses/${id}`;
    return await FastApiService.put(endpoint, payload);
  };

  deleteCourse = async (id) => {
    const endpoint = `courses/${id}`;
    return await HttpService.delete(endpoint);
  };

  getPublicCourseInfo = async (course_id) => {
    const baseUrl = process.env.REACT_APP_FASTAPI_BACKEND_URL;
    const response = await fetch(`${baseUrl}/publicCourseInfo/${course_id}`);
    if (!response.ok) throw new Error(`HTTP error ${response.status}`);
    return response.json();
  };

  getAvailableCourses = async (id) => {
    const endpoint = `availableCourses/${id}`;
    return await FastApiService.get(endpoint);
  };

  updateAvailableCourses = async (payload) => {
    const endpoint = `availableCourses`;
    return await FastApiService.post(endpoint, payload);
  };

  getCourseCompletionStatus = async (course_id, user_id, level) => {
    const endpoint = `courseCompletionStatus/${user_id}/${course_id}/${level}`;
    return await FastApiService.get(endpoint);
  };



  getCoursePurchaseStatus = async (course_id, user_id) => {
    const endpoint = `coursePurchaseStatus/${user_id}/${course_id}`;
    return await FastApiService.get(endpoint);
  };

  getQuizCertificate = async (course_id, user_id, level) => {
    const endpoint = `courses/${course_id}/quiz_certificate/${user_id}/${level}`;
    return await FastApiService.get(endpoint);
  };

  getShowCertificate = async (course_id, user_id, level) => {
    const endpoint = `courses/${course_id}/show_certificate/${user_id}/${level}`;
    return await FastApiService.get(endpoint);
  };

  getQuestions = async (course_id) => {
    const endpoint = `courses/${course_id}/questions`;
    return await FastApiService.get(endpoint);
  };

  // getUserQuestions = async (course_id, user_id) => {
  //   const endpoint = `courses/${course_id}/${user_id}/questions`;
  //   return await FastApiService.get(endpoint);
  // };

  postUserQuestionsByLevel = async (course_id, user_id, level) => {
    const formData = new FormData();
    formData.append("level", level);
    const endpoint = `courses/${course_id}/${user_id}/questions`;
    const response = await FastApiService.postWithHeaders(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  };

  submitQuiz = async (payload) => {
    const endpoint = "submitQuiz";
    return await FastApiService.post(endpoint, payload);
  };

  completeModule = async (user_id, course_id, module_id) => {
    const endpoint = `complete_module/${user_id}/${course_id}`;
    return await FastApiService.patch(endpoint, { module_id });
  };

  getCompletedModules = async (user_id, course_id) => {
    const endpoint = `completed_modules/${user_id}/${course_id}`;
    return await FastApiService.get(endpoint);
  };

  // Resume related functions
  getLatestResume = async (id) => {
    const endpoint = `resumes/${id}/latest`;
    try {
      const response = await FastApiService.getBlobResponse(endpoint);
      return response;
    } catch (error) {
      return null;
    }
  };

  getAllResumes = async (id) => {
    const endpoint = `resumes/${id}`;
    return await FastApiService.get(endpoint);
  };

  getResume = async (resumePath) => {
    const resumePathEncoded = encodeURIComponent(resumePath);
    const endpoint = `specificResume/${resumePathEncoded}`;
    return await FastApiService.getBlobResponse(endpoint);
  };

  uploadResume = async (userId, file) => {
    const endpoint = "uploadResume";
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", userId);
    const response = await FastApiService.postWithHeaders(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  };

  parseResume = async (userId, file) => {
    const endpoint = "parseResume";
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", userId);
    const response = await FastApiService.postWithHeaders(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  };

  // Certificate related functions
  getCertificates = async (id) => {
    const endpoint = `certificates/${id}`;
    return await FastApiService.get(endpoint);
  };

  // Profile related functions
  getProfileSuggestions = async (payload) => {
    const endpoint = "get_profile_suggestions";
    return await FastApiService.post(endpoint, {
      linkedin_profile_id: payload,
    });
  };

  getProfileSummary = async (user_id) => {
    const endpoint = `profile_summary/${user_id}`;
    return await FastApiService.get(endpoint);
  };

  // Chatbot related functions
  getChatbotResponse = async (payload) => {
    const endpoint = "chat";
    return await FastApiService.post(endpoint, payload);
  };

  // Recommendation related functions
  getRecommendedCourses = async (id) => {
    const endpoint = `recommendedCourses/${id}`;
    const response = await FastApiService.get(endpoint);
    return response;
  };

  // Course generation related functions
  generateCourseOutline = async (payload) => {
    const endpoint = "generate_course_outline";
    return await FastApiService.post(endpoint, payload);
  };

  generateCoverLetter = async (payload) => {
    const endpoint = "generate_cover_letter";
    return await FastApiService.post(endpoint, payload);
  };

  generateSkillMatchScore = async (payload) => {
    const endpoint = "generate_skill_match_score";
    return await FastApiService.post(endpoint, payload);
  };

  // Course purchase related functions
  purchaseCourse = async (payload) => {
    const endpoint = "purchaseCourse";
    return await FastApiService.post(endpoint, payload);
  };

  // Deals related functions
  getDeals = async () => {
    const endpoint = "deals";
    return await HttpService.get(endpoint);
  };

  getDeal = async (id) => {
    const endpoint = `deals/${id}`;
    return await HttpService.get(endpoint);
  };

  createDeal = async (payload) => {
    const endpoint = "deals";
    return await HttpService.post(endpoint, payload);
  };

  deleteDeal = async (id) => {
    const endpoint = `deals/${id}`;
    return await HttpService.delete(endpoint);
  };

  purchaseDeal = async (payload) => {
    const endpoint = "deals/purchaseDeal";
    return await HttpService.post(endpoint, payload);
  };

  // feedback related functions
  postFeedback = async (payload) => {
    const endpoint = "feedback";
    return await HttpService.post(endpoint, payload);
  };

  // coursify job related functions
  getCoursifyJobs = async (userId) => {
    const endpoint = "coursifyJob/" + userId;
    return await HttpService.get(endpoint);
  };

  createCoursifyJob = async (payload) => {
    const endpoint = "coursifyJob";
    return await HttpService.post(endpoint, payload);
  };

  deleteCoursifyJob = async (id) => {
    const endpoint = `coursifyJob/${id}`;
    return await HttpService.delete(endpoint);
  };

  // coursify privileges related functions

  // get coursify privileges for the user
  getUserCoursifyPrivileges = async (userId) => {
    const endpoint = "users/coursifyPrivileges/" + userId;
    return await HttpService.get(endpoint);
  };

  // request course privileges to admin from user
  requestCoursifyPrivileges = async (userId) => {
    const endpoint = "users/requestCoursifyPrivileges/" + userId;
    return await HttpService.post(endpoint);
  };

  // manage coursify privileges for the user; only admin can call this function
  // action = approve, reject
  manageCoursifyPrivileges = async (userId, action) => {
    if (action !== "approve" && action !== "reject") {
      return;
    }
    const endpoint = `users/manageCoursifyPrivileges/${userId}/${action}`;
    return await HttpService.post(endpoint);
  };

  applyCouponCode = async (payload) => {
    const endpoint = "coupons/applyCoupon";
    return await HttpService.post(endpoint, payload);
  };

  // get all the coupons
  getCoupons = async () => {
    const endpoint = "coupons";
    return await HttpService.get(endpoint);
  };

  // update a coupon
  updateCoupon = async (payload, id) => {
    const endpoint = `coupons/${id}`;
    return await HttpService.patch(endpoint, payload);
  };

  // create a coupon
  createCoupon = async (payload) => {
    const endpoint = "coupons";
    return await HttpService.post(endpoint, payload);
  };

  // delete a coupon
  deleteCoupon = async (id) => {
    const endpoint = `coupons/${id}`;
    return await HttpService.delete(endpoint);
  };

  // check if coupon exists
  checkCoupon = async (couponCode) => {
    const endpoint = `coupons/checkCoupon`;
    return await HttpService.post(endpoint, { couponCode: couponCode });
  };

  // Custom feedback endpoint related functions
  getCustomFeedbackEndpoints = async () => {
    const endpoint = "feedback/endpoints";
    return await HttpService.get(endpoint);
  };

  createCustomFeedbackEndpoint = async (payload) => {
    const endpoint = "feedback/endpoints";
    return await HttpService.post(endpoint, payload);
  };

  updateCustomFeedbackEndpoint = async (id, payload) => {
    const endpoint = `feedback/endpoints/${id}`;
    return await HttpService.patch(endpoint, payload);
  };

  publishCustomFeedbackEndpoint = async (id) => {
    const endpoint = `feedback/endpoints/${id}/publish`;
    return await HttpService.post(endpoint);
  };

  unpublishCustomFeedbackEndpoint = async (id) => {
    const endpoint = `feedback/endpoints/${id}/unpublish`;
    return await HttpService.post(endpoint);
  };

  deleteCustomFeedbackEndpoint = async (id) => {
    const endpoint = `feedback/endpoints/${id}`;
    return await HttpService.delete(endpoint);
  };

  getCustomFeedbackEndpointResponses = async (id) => {
    const endpoint = `feedback/endpoints/${id}/responses`;
    return await HttpService.get(endpoint);
  };

  getAgg10m = async (duration) => {
    const endpoint = `metrics/agg10m`;
    return await FastApiService.get(endpoint, { params: { duration: duration } });
  };

  getAggDaily = async (period) => {
    const endpoint = `metrics/aggDaily`;
    return await FastApiService.get(endpoint, { params: { period: period } });
  };
}

export default new CrudService();
